import React, { useState, useMemo } from 'react';
import { Box, Container, Typography, Toolbar, TextField, InputAdornment, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import FooterSection from '../components/FooterSection';

const sections = [
  {
    title: 'General',
    faqs: [
      {
        q: 'What is Sigmatiqa and how does it work?',
        a: 'Sigmatiqa is a cloud-based temperature monitoring platform designed for facilities management and Legionella risk management. Our wireless IoT sensors collect real-time temperature data, which is securely transmitted to our cloud dashboard. You can access live readings, historical data, and compliance reports from any device, and set up configurable alerts to notify you of out-of-spec conditions.',
      },
      {
        q: 'What types of sensors do you offer?',
        a: 'We offer two main sensor types: Darwin, a non-invasive pipe temperature sensor with up to 3 probes for monitoring hot and cold water pipes; and Eden, a cold-chain temperature sensor designed for fridges, freezers, and controlled environments. Both sensors are wireless, battery-powered, and designed for easy installation.',
      },
      {
        q: 'Which industries can use Sigmatiqa sensors?',
        a: 'Our sensors are ideal for a wide range of industries including healthcare, hospitality, education, manufacturing, and any facility that requires temperature monitoring for compliance or operational efficiency.',
      },
      {
        q: 'What technologies power Sigmatiqa?',
        a: 'Our IoT sensors use LoRaWAN for long-range, low-power wireless communication. The cloud platform is built on secure, scalable infrastructure with a user-friendly dashboard for data visualisation and management.',
      },
      {
        q: 'What wireless communication methods are used?',
        a: 'Sigmatiqa sensors support reliable long-range wireless protocols for data transfer and short-range wireless communication for deployment.',
      },
      {
        q: 'What wireless technology do the sensors use?',
        a: 'Our sensors use LoRaWAN operating on the 868 MHz band (EU868). This provides long-range connectivity - typically upto 100 meters inside densely built buildings and >1 km in open spaces - with minimal power consumption.',
      },
      {
        q: 'Do I need an on-site gateway?',
        a: 'Yes. Each site requires at least one LoRaWAN gateway to aggregate sensor data and forward it to the cloud. Gateways connect via Ethernet, WiFi, or4G and require a mains power supply. We supply and configure gateways as part of our standard installation packages.',
      },
      {
        q: 'Do I need internet access on-site for the sensors to work?',
        a: 'Yes. The sensors require an internet connection to transmit data to our cloud platform. This is typically achieved via the gateway that connects to your network via Ethernet or WiFi.',
      },
      {
        q: 'How long do the batteries last?',
        a: 'Our sensors are designed for long-life operation, lasting 3+ years even at a 1-minute reporting interval. Battery level is reported remotely so you can plan replacements in advance.',
      },
      {
        q: 'How is my data secured?',
        a: 'All data is encrypted during transmission and storage, following industry-standard security practices. Our cloud infrastructure is hosted in UK-based data centres and we are committed to GDPR compliance.',
      },
      {
        q: 'Is on-site maintenance required?',
        a: 'No. Our sensors are designed for minimal maintenance. Battery replacements are infrequent, mainly periodic battery checks or replacements when alerted by the dashboard.',
      },
      {
        q: 'Is there a minimum contract term?',
        a: 'We offer annual subscriptions. Hardware can be purchased outright or included in a bundled annual plan. Contact us to discuss what works best for your organisation.',
      },
      {
        q: 'What happens if a sensor goes offline?',
        a: 'If a sensor stops reporting for a configurable period (default 1 hour), the platform generates an offline alert via email so you can investigate promptly.',
      },
    ],
  },
  {
    title: 'Dashboard',
    faqs: [
      {
        q: 'What can I see on the Sigmatiqa dashboard?',
        a: 'Our dashboard provides real-time monitoring of all your sensors, historical data visualisation, configurable alerts, and compliance report generation. You can view live readings, set up rules for temperature thresholds and flow events, and export data for audits.',
      },
      {
        q: 'Can I monitor multiple sites from the same dashboard?',
        a: 'Yes. You can manage multiple sites and locations from a single dashboard account, making it easy to oversee all your monitoring points in one place.',
      },
      {
          q: 'Can I customise alerts and thresholds?',
          a: 'Yes. You can customise alerts and thresholds for each sensor on the dashboard, allowing you to receive notifications based on specific conditions that are relevant to your operations.',
      },
      {
        q: 'Can I integrate it with my existing BMS or CAFM system?',
        a: 'Yes. We provide a REST API for data collection and integration with your Building Management Systems. Contact us for details on your specific system.',
      },
      {
        q: 'How long is data stored on the dashboard?',
        a: 'All sensor data is stored securely for up to 5 years, giving you full access to historical readings and compliance records whenever you need them.',
      },
      {
        q: 'Can I export compliance reports?',
        a: 'Yes. You can export detailed compliance reports at any time from the Reports section of your dashboard, in formats suitable for audits and regulatory submissions.',
      },
      {
        q: 'Can multiple users access the dashboard?',
        a: 'Yes. You can create multiple user accounts with different roles and permissions, allowing team members to access the dashboard according to their responsibilities.',
      },
    ],
  },
  {
    title: 'App',
    faqs: [
      {
        q: 'What is the app used for?',
        a: 'The Sigmatiqa Sensor Hub app is used to provision sensors, assign them to sites and locations, configure device settings, and manage installations.',
      },
      {
        q: 'Which devices does the app support?',
        a: 'The app is available for both iOS and Android and requires Bluetooth to communicate with the sensors and an internet connection to update the dashboard during setup.',
      },
      {
        q: 'What is provisioning?',
        a: 'Provisioning is the process of configuring a sensor via the Sigmatiqa Sensor Hub app to connect to your private network gateway. Without provisioning, your sensor cannot collect and store your temperature measurements.',
      },
      {
        q: 'What is installing?',
        a: 'Installing is the process of assigning a provisioned sensor to a specific site and location on your dashboard, giving it a meaningful name, and setting any necessary configuration such as temperature offsets.',
      },
      {
        q: 'My devices are all uninstalled. What should I do?',
        a: 'Don\'t worry. If the devices are provisioned, no sensor data will be lost. If any of your devices are uninstalled, you will need to install them using the Sigmatiqa Sensor Hub app. Click on the uninstalled device and follow the install quick action. Once successfully installed, the device will appear on your dashboard. If you have any issues during installation, please contact our support team for assistance.',
      },
      {
        q: 'I want to change the name of my sensor. How do I do that?',
        a: 'To change the name of your sensor, open the Sigmatiqa Sensor Hub app, navigate to the device, and install it again giving it the new name during the installation process. The updated name will then be reflected on your dashboard.',
      }
    ],
  },
  {
    title: 'Darwin',
    faqs: [
      {
        q: 'What does the Darwin sensor monitor?',
        a: 'Darwin is a non-invasive pipe temperature sensor with up to 3 probes per device. It measures surface temperature on hot and cold water pipes to support failure recognition and applications such as Legionella risk management.',
      },
      {
        q: 'Who typically uses Darwin sensors?',
        a: 'Darwin sensors are typically used by, but not limited to, facilities managers, maintenance teams, and health and safety officers to monitor pipe temperatures and ensure compliance with regulatory standards.',
      },
      {
        q: 'Can it replace manual water temperature checks?',
        a: 'Yes. Darwin provides continuous, automated monitoring of pipe temperatures, reducing the need for manual checks and providing more comprehensive data for compliance and risk management.',
      },
      {
        q: 'Can it identify low-use outlets and stagnation risk?',
        a: 'Yes. Darwin can identify low-use outlets and potential stagnation risks by monitoring temperature variations and flow patterns, helping to prevent Legionella growth.',
      },
      {
        q: 'How does the sensor measure pipe temperature?',
        a: 'The sensor uses an external probe that is securely attached to the pipe surface to measure temperature accurately. It does not require any invasive installation or contact with the water inside the pipe.',
      },
      {
        q: 'Why is pipe temperature monitoring important for Legionella risk management?',
        a: 'Monitoring pipe temperatures is crucial for preventing Legionella growth, as it allows you to maintain water systems within safe temperature ranges and detect potential risks early.',
      },
      {
        q: 'Is the sensor suitable for hot and cold water pipes?',
        a: 'Yes. Darwin is designed to monitor both hot and cold water pipes, making it a versatile solution for comprehensive water system monitoring.',
      },
      {
        q: 'Can Darwin be used for applications other than Legionella risk management?',
        a: 'Yes. While Darwin is primarily designed for Legionella risk management, it can also be used for other applications that require accurate pipe temperature monitoring.',
      },
      {
        q: 'What pipe sizes can Darwin monitor?',
        a: 'Darwin can monitor a wide range of pipe sizes, from small domestic pipes to larger commercial systems. The probe can be securely attached to pipes of various diameters using the supplied fixings or self-amalgamating tape.',
      },
      {
        q: 'How accurate are the temperature readings?',
        a: 'Darwin provides accurate temperature readings with an accuracy of ±0.3°C, ensuring reliable data for monitoring and compliance purposes.',
      },
      {
        q: 'Can Darwin help us comply with HSG274 and HTM 04-01?',
        a: 'Yes. Our Darwin sensors and cloud platform are designed to support Legionella risk management programmes under HSG274 and NHS facilities management under HTM 04-01. Automated logging, threshold alerts, and compliance report exports help you maintain accurate records and stay audit-ready.',
      },
      {
        q: 'Can Darwin detect water flow?',
        a: 'Yes. Darwin can detect flow and no-flow events by monitoring temperature changes on the pipe surface, enabling you to set flow-based rules and alerts.',
      },
      {
        q: 'Can the sensor data be used for compliance reporting?',
        a: 'Yes. All data collected by Darwin sensors is securely stored and can be exported in compliance-ready formats for audits and regulatory submissions.',
      },
      {
        q: 'How can Darwin save me time and reduce manual checks?',
        a: 'Darwin provides continuous, automated monitoring of pipe temperatures, reducing the need for manual checks and providing more comprehensive data for compliance and risk management.',
      },
      {
        q: 'How can Darwin save money?',
        a: 'By providing continuous monitoring and early alerts for out-of-spec conditions, Darwin can help prevent costly incidents related to Legionella growth and non-compliance penalties. By reducing the need for manual checks, it can also save time and labour costs associated with traditional monitoring methods.',
      },
      {
        q: 'How is the sensor powered?',
        a: 'Darwin is powered by a long-life battery designed for continuous monitoring. Battery level is monitored remotely, and you will receive alerts when low-battery detections are made.',
      },
      {
        q: 'How long does the battery last?',
        a: 'Battery life can typically last up to 5 years depending on usage.'
      }
    ],
  },
  {
    title: 'Eden',
    faqs: [
      {
        q: 'What does the Eden sensor monitor?',
        a: 'Eden is a temperature sensor designed for fridges, freezers, and controlled environments. It operates across a range of -40°C to +40°C with accuracy better than ±0.3°C.',
      },
      {
        q: 'Who is the product for?',
        a: 'Eden is ideal for restaurants, hotels, laboratories, pharmacies, facilities managers, quality assurance teams, and health and safety officers responsible for monitoring cold-chain environments and ensuring compliance with temperature-sensitive regulations.',
      },
      {
        q: 'How does the sensor measure temperature?',
        a: 'The sensor has an integrated probe that measures ambient temperature with an accuracy of ±0.3°C, making it suitable for monitoring fridges, freezers, and other controlled environments.',
      },
      {
        q: 'Can it monitor both fridges and freezers?',
        a: 'Yes. Eden is designed to monitor a wide range of cold-chain environments, including both fridges and freezers, ensuring your temperature-sensitive assets are protected.',
      },
      {
        q: 'How accurate are the temperature readings?',
        a: 'Eden provides accurate temperature readings with an accuracy of ±0.3°C, ensuring reliable data for monitoring and compliance purposes.',
      },
      {
        q: 'Can Eden help us comply with food safety regulations?',
        a: 'Yes. Eden is designed to support compliance with food safety regulations by providing continuous temperature monitoring, configurable alerts, and compliance report exports to help you maintain accurate records and stay audit-ready.',
      },
      {
        q: 'Is the sensor easy to install?',
        a: 'Yes. Eden is designed for easy installation, allowing you to quickly set up the sensor in your fridges, freezers, or other controlled environments without the need for specialised tools or expertise.',
      },
      {
        q: 'How long does the battery last?',
        a: 'Eden is powered by a long-life battery designed for continuous monitoring, with an expected battery life of up to 5 years depending on usage. Battery level is monitored remotely, and you will receive alerts when low-battery detections are made.',
      },
      {
        q: 'How can Eden save me time and reduce manual checks?',
        a: 'Eden provides continuous, automated monitoring of temperature-sensitive environments, reducing the need for manual checks and providing more comprehensive data for compliance and risk management.',
      },
      {
        q: 'Can the sensor data be used for compliance reporting?',
        a: 'Yes. All data collected by Eden sensors is securely stored and can be exported in compliance-ready formats for audits and regulatory submissions.',
      },
      {
        q: 'Why choose Eden over traditional temperature monitoring methods?',
        a: 'Eden offers real-time monitoring, automated alerts, and comprehensive data logging, providing a more efficient and reliable solution compared to traditional manual checks or standalone data loggers.',
      },
      {
        q: 'How can Eden save money?',
        a: 'By providing continuous monitoring and early alerts for out-of-spec conditions, Eden can help prevent costly spoilage of temperature-sensitive goods and reduce the risk of non-compliance penalties. By reducing the need for manual checks, it can also save time and labour costs associated with traditional monitoring methods.',
      },
      {
        q: 'Can I add more sensors later?',
        a: 'Yes. You can easily add more Eden sensors to your existing setup, allowing you to expand your monitoring capabilities as needed.',
      }
    ],
  },
  {
    title: 'Rules',
    faqs: [
      {
        q: 'What is the Rules Engine?',
        a: 'The Rules Engine is an automated monitoring feature that checks sensor data every hour and identifies conditions that may require attention. It helps users stay informed by detecting issues early and generating alerts and dashboard notifications.'
      },
      {
        q: 'What types of checks can the Rules Engine perform?',
        a: 'The system can run a range of automated checks, including temperature checks - alerts when readings are above, below, or equal to set thresholds; delta checks - compares 2 sensor readings to identify when temperatures differences are more, less, or equal to a configured threshold; flow checks - identifies flow and sends alerts (Darwin only); and no-flow checks - identifies when there has not been any flow for a specified period (Darwin only).',
      },
      {
        q: 'What types of rules can I configure?',
        a: 'You can set up rules for temperature thresholds, temperature delta (temperature difference between more than one sensor or probe), flow, and no-flow. Flow and no-flow rules are available for Darwin sensors only.',
      },
      {
        q: 'How am I notified when a rule triggers?',
        a: 'You will receive an email notification whenever a configured rule is triggered. Alerts are also logged on your dashboard for a complete audit trail.',
      },
      {
        q: 'Can I view triggered rules and alerts on the dashboard?',
        a: 'Yes. All triggered rules and alerts are logged on your dashboard, allowing you to review incidents, track patterns, and maintain a complete record for compliance purposes.',
      },
      {
        q: 'How does the Rules Engine help with compliance?',
        a: 'The Rules Engine helps you maintain compliance by providing automated monitoring and alerts for conditions that may indicate risks or non-compliance. By identifying issues early, you can take corrective actions promptly and maintain accurate records for audits.',
      },
      {
        q: 'Can different sensors have different rules configured?',
        a: 'Yes. You can configure different rules for each sensor or probe, allowing you to tailor monitoring and alerts to the specific conditions and requirements of each location.',
      },
      {
        q: 'Can the Rules Engine help reduce downtime or losses?',
        a: 'Yes. By providing real-time monitoring and early alerts for out-of-spec conditions, the Rules Engine can help you prevent incidents that may lead to downtime, spoilage, or other losses, ultimately saving you time and money.',
      }
    ],
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        borderBottom: '1px solid #e8e8e8',
        py: 0,
        cursor: 'pointer',
      }}
      onClick={() => setOpen((o) => !o)}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2.5,
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontWeight: 600,
            fontSize: { xs: '0.97rem', md: '1.05rem' },
            color: '#000000',
            lineHeight: 1.5,
          }}
        >
          {q}
        </Typography>
        <Box sx={{ flexShrink: 0, color: '#004D40' }}>
          {open ? <RemoveIcon /> : <AddIcon />}
        </Box>
      </Box>
      {open && (
        <Typography
          sx={{
            fontFamily: 'Outfit',
            fontWeight: 400,
            fontSize: '0.95rem',
            color: '#555555',
            lineHeight: 1.8,
            pb: 2.5,
            pr: { md: 6 },
          }}
        >
          {a}
        </Typography>
      )}
    </Box>
  );
};

const sectionTitles = sections.map((s) => s.title);

const FAQsPage = () => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const term = search.toLowerCase();
    return sections
      .map((section) => ({
        ...section,
        faqs: section.faqs.filter(
          (faq) =>
            faq.q.toLowerCase().includes(term) ||
            faq.a.toLowerCase().includes(term)
        ),
      }))
      .filter((section) => section.faqs.length > 0);
  }, [search]);

  // When searching, show all matching sections; otherwise show the active tab
  const visibleSections = search.trim()
    ? filteredSections
    : filteredSections.filter((s) => s.title === sectionTitles[activeTab]);

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Page hero */}
      <Box sx={{ backgroundColor: '#000000', py: { xs: 8, md: 12 }, borderBottom: '1px solid #1a1a1a' }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 480,
            }}
          >
            Everything you need to know about our sensors, platform, and
            getting started.
          </Typography>
        </Container>
      </Box>

      {/* Search + FAQ sections */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          {/* Search bar */}
          <TextField
            fullWidth
            placeholder="Search FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            sx={{
              mb: 4,
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Outfit',
                fontSize: '0.97rem',
                borderRadius: '8px',
                '& fieldset': { borderColor: '#e8e8e8' },
                '&:hover fieldset': { borderColor: '#004D40' },
                '&.Mui-focused fieldset': { borderColor: '#004D40' },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#aaaaaa' }} />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Section tabs */}
          {!search.trim() && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                mb: 5,
              }}
            >
              {sectionTitles.map((title, i) => (
                <Chip
                  key={title}
                  label={title}
                  size="small"
                  onClick={() => setActiveTab(i)}
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    backgroundColor: activeTab === i ? '#004D40' : 'transparent',
                    color: activeTab === i ? '#ffffff' : '#555555',
                    border: activeTab === i ? '1px solid #004D40' : '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: activeTab === i ? '#00695C' : 'rgba(0,77,64,0.06)',
                    },
                    transition: 'all 0.2s',
                  }}
                />
              ))}
            </Box>
          )}

          {visibleSections.length === 0 && (
            <Typography
              sx={{
                fontFamily: 'Outfit',
                fontSize: '1rem',
                color: '#999999',
                textAlign: 'center',
                py: 4,
              }}
            >
              No FAQs match your search.
            </Typography>
          )}

          {visibleSections.map((section) => (
            <Box key={section.title} sx={{ mb: 6 }}>
              {search.trim() && (
                <Chip
                  label={section.title}
                  size="small"
                  sx={{
                    fontFamily: 'Outfit',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    mb: 2,
                    backgroundColor: '#004D40',
                    color: '#ffffff',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                />
              )}
              <Box sx={{ borderTop: '1px solid #e8e8e8' }}>
                {section.faqs.map((faq) => (
                  <FAQItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </Box>
            </Box>
          ))}
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default FAQsPage;
